import { useMemo, useState } from 'react'
import { CalendarClock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { DATE_UNITS, formulaToHuman, type DateUnit } from '@/lib/formula'
import {
  OperandInput,
  emptyOperand,
  serializeOperand,
  type ConditionOperand,
  type OperandState,
} from './ConditionBuilder'

const UNIT_LABELS: Record<DateUnit, string> = {
  days: 'days',
  hours: 'hours',
  minutes: 'minutes',
  weeks: 'weeks',
  months: 'months',
  years: 'years',
}

interface Props {
  /** Which date function this instance builds. */
  fn: 'DATEADD' | 'DATEDIFF'
  /** Fields (or library placeholders) offered in the operand pickers. */
  operands: ConditionOperand[]
  /** Library mode: allow typing a brand-new placeholder name in any slot. */
  allowCustomRefs?: boolean
  /** Called with the complete, valid DATEADD(...)/DATEDIFF(...) expression. */
  onInsert: (formula: string) => void
  onCancel: () => void
  className?: string
}

/**
 * Structured DATEADD(date, amount, unit) / DATEDIFF(start, end, unit) editor —
 * mirrors AggregateBuilder/ConditionBuilder's approach of only emitting the
 * formula once every slot is filled. TODAY()/NOW() are typed into a date
 * slot via its "Expression…" option (e.g. `TODAY()`), the same escape hatch
 * OperandInput already offers for any advanced sub-expression.
 */
export function DateFunctionBuilder({
  fn,
  operands,
  allowCustomRefs,
  onInsert,
  onCancel,
  className,
}: Props) {
  const isAdd = fn === 'DATEADD'
  const [dateOrStart, setDateOrStart] = useState<OperandState>(emptyOperand())
  const [amountOrEnd, setAmountOrEnd] = useState<OperandState>(emptyOperand())
  const [unit, setUnit] = useState<DateUnit>('days')

  const serialized = useMemo(() => {
    const a = serializeOperand(dateOrStart)
    const b = serializeOperand(amountOrEnd)
    if (!a || !b) return null
    return `${fn}(${a}, ${b}, "${unit}")`
  }, [fn, dateOrStart, amountOrEnd, unit])

  const preview = useMemo(() => {
    if (!serialized) return null
    const labels: Record<string, string> = {}
    for (const o of operands) labels[o.id] = o.label
    for (const m of serialized.matchAll(/\{([^}]+)\}/g)) {
      if (!(m[1] in labels)) labels[m[1]] = m[1]
    }
    return formulaToHuman(serialized, labels)
  }, [serialized, operands])

  return (
    <div className={cn('space-y-2 rounded-md border bg-card p-2.5', className)}>
      <div className='flex items-center gap-1.5'>
        <CalendarClock className='h-3.5 w-3.5 text-muted-foreground' />
        <span className='text-xs font-semibold'>
          Build {isAdd ? 'DATEADD' : 'DATEDIFF'} expression
        </span>
      </div>

      <div className='space-y-1.5'>
        <p className='text-[11px] text-muted-foreground'>
          {isAdd ? 'Date' : 'Start date'} — pick a field, or choose "Expression…" and type{' '}
          <span className='font-mono'>TODAY()</span> or <span className='font-mono'>NOW()</span>.
        </p>
        <OperandInput
          value={dateOrStart}
          onChange={setDateOrStart}
          operands={operands}
          allowCustomRefs={allowCustomRefs}
          placeholder={isAdd ? 'Date…' : 'Start date…'}
        />
      </div>

      <div className='space-y-1.5'>
        <p className='text-[11px] text-muted-foreground'>
          {isAdd ? 'Amount to add (negative to subtract)' : 'End date'}
        </p>
        <OperandInput
          value={amountOrEnd}
          onChange={setAmountOrEnd}
          operands={operands}
          allowCustomRefs={allowCustomRefs}
          placeholder={isAdd ? 'Amount…' : 'End date…'}
        />
      </div>

      <div className='space-y-1.5'>
        <p className='text-[11px] text-muted-foreground'>Unit</p>
        <Select value={unit} onValueChange={v => setUnit(v as DateUnit)}>
          <SelectTrigger className='h-8 text-xs'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_UNITS.map(u => (
              <SelectItem key={u} value={u}>
                {UNIT_LABELS[u]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {preview ? (
        <div className='rounded bg-muted/40 px-2 py-1.5'>
          <p className='text-[10px] uppercase tracking-wide text-muted-foreground'>Preview</p>
          <p className='text-xs font-medium break-words'>{preview}</p>
        </div>
      ) : (
        <p className='text-[11px] text-muted-foreground'>Fill every value to enable Insert.</p>
      )}

      <div className='flex items-center justify-end gap-2'>
        <Button type='button' size='sm' variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='button'
          size='sm'
          disabled={!serialized}
          onClick={() => serialized && onInsert(serialized)}
        >
          Insert {isAdd ? 'date add' : 'date diff'}
        </Button>
      </div>
    </div>
  )
}
